import React, { useState, useEffect, useMemo } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
  Label,
} from '../ui'
import {
  useCreateMonthlyStaffingLimit,
  useUpdateMonthlyStaffingLimit,
  type MonthlyStaffingLimit,
  type CreateMonthlyStaffingLimitData,
  type UpdateMonthlyStaffingLimitData,
} from '../../hooks/useStaffingLimits'
import { usePositions } from '../../hooks/usePositions'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { LoadingSpinner } from '../ui/loading-states'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

interface MonthlyStaffingLimitFormData {
  organization_id: string
  position_id: string
  department_id: string | null
  year: number
  month: number
  max_hours: number
  max_shifts: number | null
}

interface MonthlyStaffingLimitFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  limit?: MonthlyStaffingLimit | null
  mode: 'create' | 'edit'
}

export function MonthlyStaffingLimitForm({ open, onOpenChange, limit, mode }: MonthlyStaffingLimitFormProps) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  const [formData, setFormData] = useState<MonthlyStaffingLimitFormData>({
    organization_id: '',
    position_id: '',
    department_id: null,
    year: currentYear,
    month: currentMonth,
    max_hours: 160,
    max_shifts: null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load data for selection
  const { data: positionsData } = usePositions({ limit: 100 })
  const { data: organizationsData } = useOrganizationsForSelect()
  // Подразделения фильтруются по выбранной организации
  const { data: departmentsData } = useDepartmentsForSelect(formData.organization_id)

  const createLimit = useCreateMonthlyStaffingLimit()
  const updateLimit = useUpdateMonthlyStaffingLimit()

  const isLoading = createLimit.isPending || updateLimit.isPending

  // Generate year options
  const yearOptions = useMemo(() => {
    const years = []
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
      years.push(y)
    }
    return years
  }, [currentYear])

  // Initialize form data when dialog opens or limit changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && limit) {
        setFormData({
          organization_id: limit.organization_id || '',
          position_id: limit.position_id || '',
          department_id: limit.department_id || null,
          year: limit.year || currentYear,
          month: limit.month || currentMonth,
          max_hours: limit.max_hours || 0,
          max_shifts: limit.max_shifts ?? null,
        })
      } else {
        setFormData({
          organization_id: organizationsData?.[0]?.id || '',
          position_id: '',
          department_id: null,
          year: currentYear,
          month: currentMonth,
          max_hours: 160,
          max_shifts: null,
        })
      }
      setErrors({})
      createLimit.reset()
      updateLimit.reset()
    }
  }, [open, mode, limit, organizationsData, currentYear, currentMonth])

  const handleInputChange = (field: keyof MonthlyStaffingLimitFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value: string | number | null = e.target.value

    // Convert empty string to null for department_id
    if (value === '' && field === 'department_id') {
      value = null
    }

    // Convert numeric fields
    if (['year', 'month', 'max_hours'].includes(field)) {
      value = parseInt(e.target.value, 10) || 0
    }
    if (field === 'max_shifts') {
      value = e.target.value === '' ? null : parseInt(e.target.value, 10) || 0
    }

    // При смене организации сбрасываем выбранное подразделение
    if (field === 'organization_id') {
      setFormData(prev => ({ ...prev, organization_id: value as string, department_id: null }))
      return
    }

    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.organization_id) {
      newErrors.organization_id = 'Организация обязательна'
    }

    if (!formData.position_id) {
      newErrors.position_id = 'Должность обязательна'
    }

    if (!formData.year || formData.year < 2000 || formData.year > 2100) {
      newErrors.year = 'Укажите корректный год'
    }

    if (!formData.month || formData.month < 1 || formData.month > 12) {
      newErrors.month = 'Укажите корректный месяц'
    }

    // Достаточно одного из лимитов: часы или смены
    const hasHours = formData.max_hours >= 1
    const hasShifts = formData.max_shifts !== null && formData.max_shifts >= 1
    if (!hasHours && !hasShifts) {
      newErrors.max_hours = 'Укажите лимит часов и/или лимит смен (не менее 1)'
    }
    if (formData.max_shifts !== null && formData.max_shifts < 1) {
      newErrors.max_shifts = 'Лимит смен должен быть не менее 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      if (mode === 'create') {
        const data: CreateMonthlyStaffingLimitData = {
          organization_id: formData.organization_id,
          position_id: formData.position_id,
          department_id: formData.department_id || null,
          year: formData.year,
          month: formData.month,
          ...(formData.max_hours >= 1 && { max_hours: formData.max_hours }),
          ...(formData.max_shifts !== null &&
            formData.max_shifts >= 1 && { max_shifts: formData.max_shifts }),
        }
        await createLimit.mutateAsync(data)
      } else if (mode === 'edit' && limit) {
        const data: UpdateMonthlyStaffingLimitData = {
          organization_id: formData.organization_id,
          position_id: formData.position_id,
          department_id: formData.department_id,
          year: formData.year,
          month: formData.month,
          ...(formData.max_hours >= 1 && { max_hours: formData.max_hours }),
          ...(formData.max_shifts !== null &&
            formData.max_shifts >= 1 && { max_shifts: formData.max_shifts }),
        }
        await updateLimit.mutateAsync({ id: limit.id, data })
      }

      onOpenChange(false)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать месячный лимит' : 'Редактировать месячный лимит'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Укажите организацию, должность, период и максимальное количество часов за месяц.'
              : 'Измените параметры месячного лимита часов.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization */}
          <div>
            <Label htmlFor="organization_id">Организация *</Label>
            <Select
              id="organization_id"
              value={formData.organization_id}
              onChange={handleInputChange('organization_id')}
              disabled={isLoading}
              className={errors.organization_id ? 'border-red-500' : ''}
            >
              <SelectOption value="">Выберите организацию</SelectOption>
              {organizationsData?.map((org: { id: string; name: string }) => (
                <SelectOption key={org.id} value={org.id}>
                  {org.name}
                </SelectOption>
              ))}
            </Select>
            {errors.organization_id && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.organization_id}
              </div>
            )}
          </div>

          {/* Position */}
          <div>
            <Label htmlFor="position_id">Должность *</Label>
            <Select
              id="position_id"
              value={formData.position_id}
              onChange={handleInputChange('position_id')}
              disabled={isLoading}
              className={errors.position_id ? 'border-red-500' : ''}
            >
              <SelectOption value="">Выберите должность</SelectOption>
              {positionsData?.data.map((position) => (
                <SelectOption key={position.id} value={position.id}>
                  {position.name}
                </SelectOption>
              ))}
            </Select>
            {errors.position_id && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.position_id}
              </div>
            )}
          </div>

          {/* Department (optional) */}
          <div>
            <Label htmlFor="department_id">Отдел (опционально)</Label>
            <Select
              id="department_id"
              value={formData.department_id || ''}
              onChange={handleInputChange('department_id')}
              disabled={isLoading || !formData.organization_id}
            >
              <SelectOption value="">
                {formData.organization_id ? 'Вся организация' : 'Сначала выберите организацию'}
              </SelectOption>
              {departmentsData?.map((dept) => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Если не указан, лимит применяется ко всей организации
            </p>
          </div>

          {/* Year and Month */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="year">Год *</Label>
              <Select
                id="year"
                value={formData.year.toString()}
                onChange={handleInputChange('year')}
                disabled={isLoading}
                className={errors.year ? 'border-red-500' : ''}
              >
                {yearOptions.map((year) => (
                  <SelectOption key={year} value={year.toString()}>
                    {year}
                  </SelectOption>
                ))}
              </Select>
              {errors.year && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.year}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="month">Месяц *</Label>
              <Select
                id="month"
                value={formData.month.toString()}
                onChange={handleInputChange('month')}
                disabled={isLoading}
                className={errors.month ? 'border-red-500' : ''}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <SelectOption key={idx + 1} value={(idx + 1).toString()}>
                    {name}
                  </SelectOption>
                ))}
              </Select>
              {errors.month && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.month}
                </div>
              )}
            </div>
          </div>

          {/* Max Hours + Max Shifts */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="max_hours">Максимум часов в месяц</Label>
              <Input
                id="max_hours"
                type="number"
                min="0"
                value={formData.max_hours}
                onChange={handleInputChange('max_hours')}
                disabled={isLoading}
                className={errors.max_hours ? 'border-red-500' : ''}
              />
              {errors.max_hours && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.max_hours}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="max_shifts">Максимум смен в месяц</Label>
              <Input
                id="max_shifts"
                type="number"
                min="1"
                value={formData.max_shifts ?? ''}
                onChange={handleInputChange('max_shifts')}
                disabled={isLoading}
                className={errors.max_shifts ? 'border-red-500' : ''}
                placeholder="—"
              />
              {errors.max_shifts && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.max_shifts}
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Достаточно одного из лимитов. Лимит смен обычно рассчитывается автоматически из штатного расписания 1С; при ручном изменении он перестаёт обновляться автосинком
          </p>

          {/* Show API errors */}
          {(createLimit.error || updateLimit.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = (createLimit.error || updateLimit.error) as any
                  const message = error?.message
                  const status = error?.status

                  if (status === 409) {
                    return 'Месячный лимит для этой должности на указанный период уже существует'
                  } else if (status === 400) {
                    return 'Проверьте правильность заполнения полей'
                  } else if (status === 403) {
                    return 'У вас нет прав для выполнения этой операции'
                  } else {
                    return message || `Произошла ошибка при ${mode === 'create' ? 'создании' : 'обновлении'} лимита`
                  }
                })()}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
